import { Attachment } from 'stream-chat';

import S3Utils from 'src/utils/S3Utils';
import { getFullTestDataFileURL } from 'src/utils/TestDataUtil';

/**
 * File attachment type with all required fields defined
 */
export interface FileAttachment extends Attachment {
  type: 'file_id';
  fields: Array<{ title: string; value: string }>;
  title: string;
}

/**
 * Extracted file attachment fields
 */
export interface FileAttachmentFields {
  fileId?: string;
  fileKey?: string;
  identityId?: string;
}

export function getAttachmentFields(
  attachment: FileAttachment,
): FileAttachmentFields {
  const getFieldValue = (fieldTitle: string) =>
    attachment.fields?.find((field) => field.title === fieldTitle)?.value;

  return {
    fileId: getFieldValue('file_id'),
    fileKey: getFieldValue('file_key'),
    identityId: getFieldValue('identity_id'),
  };
}

/**
 * Type guard to check if attachment is a valid file attachment with all required fields
 */
export function isFileAttachment(
  attachment: Attachment,
): attachment is FileAttachment {
  if (attachment.type !== 'file_id') {
    return false;
  }

  if (!attachment.fields || !Array.isArray(attachment.fields)) {
    return false;
  }

  // Check that all required fields are present with values
  const requiredFields = ['file_id', 'file_key', 'identity_id'];
  const fieldTitles = attachment.fields.map((f) => f.title);

  return requiredFields.every((required) => fieldTitles.includes(required));
}

export async function resolveFileUrl(params: {
  fileKey?: string;
  identityId?: string;
}): Promise<string | null> {
  const { fileKey, identityId } = params;

  if (fileKey && identityId) {
    // Check if it's test data
    if (fileKey.includes('public/testdata')) {
      return getFullTestDataFileURL(fileKey);
    }

    // Fetch from S3
    const s3File = await S3Utils.getFile(fileKey, {
      identityId,
      level: 'protected',
    });
    return s3File.toString();
  }

  return null;
}

/**
 * Get the appropriate icon name for a file based on its extension or MIME type
 */
export function getIconForFile(filename?: string, mimeType?: string): string {
  const extension = filename?.split('.').pop()?.toLowerCase();

  // Image extensions
  const imageExtensions = [
    'jpg',
    'jpeg',
    'png',
    'gif',
    'svg',
    'webp',
    'bmp',
    'heic',
    'tiff',
  ];

  // Check if it's an image
  if (extension && imageExtensions.includes(extension)) {
    return 'Image';
  }

  // Check mime type for images
  if (mimeType?.startsWith('image/')) {
    return 'Image';
  }

  // Everything else gets generic file icon
  return 'File';
}
